import { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from "@material-ui/core";
import { useMediaQuery } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { useFormik, FormikProvider } from "formik";
import { Icon } from "@iconify/react";
import closeFill from "@iconify/icons-eva/close-fill";
import eyeFill from "@iconify/icons-eva/eye-fill";
import PropTypes from "prop-types";
import { propuestasApi } from "src/services/propuestasApi";
import { useSnackbar } from "src/components/snackbar";
import ProposalPreview from "src/components/propuesta/ProposalPreview";
import {
  buildPropuestaPreview,
  mapItemsFormAFases,
} from "src/components/propuesta/FormularioPropuestaV2/utils/buildPropuestaPreview";
import TopBarEdicion from "./TopBarEdicion";
import NavLateralEdicion from "./NavLateralEdicion";
import BannerCambiosSinGuardar from "../shared/BannerCambiosSinGuardar";
import DialogDescartarCambios from "../shared/DialogDescartarCambios";
import SeccionPortada from "./secciones/SeccionPortada";
import SeccionQuienesSomos from "./secciones/SeccionQuienesSomos";
import SeccionAntecedentes from "./secciones/SeccionAntecedentes";
import SeccionEstructura from "./secciones/SeccionEstructura";
import SeccionFasesServicios from "./secciones/SeccionFasesServicios";
import SeccionEquipo from "./secciones/SeccionEquipo";
import SeccionInfoAdicional from "./secciones/SeccionInfoAdicional";
import { useSectionDirty } from "./hooks/useSectionDirty";
import { useSectionCompleteness } from "./hooks/useSectionCompleteness";
import { useConfirmDiscard } from "../shared/useConfirmDiscard";
import { useAnchorScroll } from "./hooks/useAnchorScroll";
import "./styles/anchorHighlight.css";
import { propuestaV2ToFormValues } from "./utils/propuestaV2ToFormValues";
import { SCROLL_CONTAINER_ID } from "./constants";
import RegenerarIADialog from "../RegenerarIADialog";

/**
 * Construye el payload PATCH /propuestas/v2/:id a partir de los values de Formik.
 *
 * Incluye `sucursal`, `vendedor` y `supervisor`
 */
const construirPayload = (values) => ({
  ...(values.solicitanteId
    ? { solicitanteId: values.solicitanteId }
    : values.solicitanteNuevo
      ? { solicitante: values.solicitanteNuevo }
      : {}),
  ...(values.sucursal ? { sucursal: values.sucursal } : {}),
  ...(values.vendedor ? { vendedor: values.vendedor } : {}),
  ...(values.supervisor ? { supervisor: values.supervisor } : {}),
  ...(values.titulo ? { titulo: values.titulo } : {}),
  ...(values.descripcion ? { descripcion: values.descripcion } : {}),
  ...(values.antecedentes ? { antecedentes: values.antecedentes } : {}),
  ...(values.resumenEjecutivo
    ? { resumenEjecutivo: values.resumenEjecutivo }
    : {}),
  ...(values.alcance ? { alcance: values.alcance } : {}),
  ...(values.condicionesPago
    ? { condicionesPago: values.condicionesPago }
    : {}),
  ...(values.duracionEstimada
    ? { duracionEstimada: values.duracionEstimada }
    : {}),
  ...(values.proximosPasos ? { proximosPasos: values.proximosPasos } : {}),
  ...(values.introduccion ? { introduccion: values.introduccion } : {}),
  ...(values.clausulaConfidencialidad
    ? { clausulaConfidencialidad: values.clausulaConfidencialidad }
    : {}),
  generadoConIA: values.generadoConIA,
  ...(values.promptOriginal ? { promptOriginal: values.promptOriginal } : {}),
  items: values.items.map((item) => ({
    tipoItem: item.tipoItem,
    refCliente: item.refCliente,
    refEmpleado: item.refEmpleado,
    nombreServicio: item.nombreServicio,
    tipoMoneda: item.tipoMoneda,
    valorOriginal: Number(item.valorOriginal),
    descuento: Number(item.descuento),
    valorConDescuento: Number(item.valorConDescuento),
    ...(item.observaciones ? { observaciones: item.observaciones } : {}),
    ...(item.refTipoPlan ? { refTipoPlan: item.refTipoPlan } : {}),
    ...(item.refTipoTarea ? { refTipoTarea: item.refTipoTarea } : {}),
    ...(item.faseTitulo ? { faseTitulo: item.faseTitulo } : {}),
    ...(item.faseObjetivo ? { faseObjetivo: item.faseObjetivo } : {}),
    ...(item.faseActividades ? { faseActividades: item.faseActividades } : {}),
    ...(item.faseEntregable ? { faseEntregable: item.faseEntregable } : {}),
    ...(item.faseDuracion ? { faseDuracion: item.faseDuracion } : {}),
    ...(item.tipoItem === "PLAN"
      ? {
          tipoFrecuencia: item.tipoFrecuencia,
          ...(item.fechaSiguienteCiclo
            ? { fechaSiguienteCiclo: item.fechaSiguienteCiclo }
            : {}),
          ...(item.mesCargaTareas
            ? { mesCargaTareas: item.mesCargaTareas }
            : {}),
          ...(item.mesFacturacion
            ? { mesFacturacion: item.mesFacturacion }
            : {}),
          seFacturaGestor: item.seFacturaGestor,
          ...(item.fechaTerminoDesc
            ? { fechaTerminoDesc: item.fechaTerminoDesc }
            : {}),
        }
      : {}),
    ...(item.tipoItem === "TAREA"
      ? {
          ...(item.fechaLimite ? { fechaLimite: item.fechaLimite } : {}),
          vendidoTaxtic: item.vendidoTaxtic,
          ...(item.fechaTerminoDesc
            ? { fechaTerminoDesc: item.fechaTerminoDesc }
            : {}),
        }
      : {}),
  })),
  equipo: values.equipo.map((m, i) => ({
    orden: i,
    rolDisplay: m.rolDisplay,
    nombre: m.nombre,
    ...(m.fotoUrl ? { fotoUrl: m.fotoUrl } : {}),
    ...(m.descripcionExperiencia
      ? { descripcionExperiencia: m.descripcionExperiencia }
      : {}),
  })),
});

const EditarPropuestaV2 = ({ propuesta }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [editarPropuestaV2, { isLoading: isSubmitting }] =
    propuestasApi.useEditarPropuestaV2Mutation();

  const [regenerarConIA, { isLoading: isRegenerando }] =
    propuestasApi.useGenerarConIAMutation();

  const [regenerarOpen, setRegenerarOpen] = useState(false);

  // Razones de selección por item generadas por IA al regenerar la propuesta.
  // Shape: { [itemIndex]: string }. Solo viven en state local — nunca se persisten.
  const [razonesSeleccionIA, setRazonesSeleccionIA] = useState({});

  const limpiarRazonSeleccionIA = (indice) => {
    setRazonesSeleccionIA((prev) => {
      if (!(indice in prev)) return prev;
      const next = { ...prev };
      delete next[indice];
      return next;
    });
  };

  const resetRazonesSeleccionIA = () => {
    setRazonesSeleccionIA({});
  };

  const swapRazonesSeleccionIA = (i, j) => {
    setRazonesSeleccionIA((prev) => {
      const a = prev[i];
      const b = prev[j];
      const next = { ...prev };
      if (a !== undefined) next[j] = a;
      else delete next[j];
      if (b !== undefined) next[i] = b;
      else delete next[i];
      return next;
    });
  };

  const handleRegenerar = async (nuevoPrompt) => {
    try {
      const resultado = await regenerarConIA({
        idea: nuevoPrompt,
        ...(formik.values.sucursal ? { sucursal: formik.values.sucursal } : {}),
      }).unwrap();

      const camposTexto = [
        "titulo",
        "descripcion",
        "antecedentes",
        "alcance",
        "condicionesPago",
        "duracionEstimada",
        "proximosPasos",
      ];
      const borrador = resultado.borrador;
      camposTexto.forEach((c) => {
        if (borrador[c]) formik.setFieldValue(c, borrador[c]);
      });
      if (borrador.fases && borrador.fases.length > 0) {
        const itemsDesdeIA = borrador.fases.map((fase) => ({
          tipoItem: fase.tipo_item === "TAREA" ? "TAREA" : "PLAN",
          nombreServicio: fase.titulo || "",
          faseTitulo: fase.titulo || "",
          faseObjetivo: fase.objetivo || "",
          faseActividades: fase.actividades || "",
          faseEntregable: fase.entregable || "",
          faseDuracion: fase.duracion || "",
          refTipoPlan: fase.ref_tipo_plan || "",
          refTipoTarea: fase.ref_tipo_tarea || "",
        }));
        formik.setFieldValue("items", itemsDesdeIA);

        const razones = borrador.fases.reduce((acc, fase, i) => {
          if (fase.razon_seleccion) acc[i] = fase.razon_seleccion;
          return acc;
        }, {});
        setRazonesSeleccionIA(razones);
      } else {
        setRazonesSeleccionIA({});
      }
      formik.setFieldValue("generadoConIA", true);
      formik.setFieldValue("promptOriginal", nuevoPrompt);

      setRegenerarOpen(false);
      enqueueSnackbar("Propuesta regenerada con IA", { variant: "success" });
    } catch {
      enqueueSnackbar(
        "No se pudo regenerar la propuesta. Intenta nuevamente.",
        { variant: "error" },
      );
    }
  };

  const initialValues = useMemo(
    () => propuestaV2ToFormValues(propuesta),
    [propuesta],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await editarPropuestaV2({
          id: propuesta.id,
          ...construirPayload(values),
        }).unwrap();
        enqueueSnackbar("Cambios guardados.", { variant: "success" });
        // invalidatesTags dispara refetch del detalle; enableReinitialize
        // resetea initialValues y dirty vuelve a false automáticamente.
      } catch (err) {
        const msg = err?.data?.message ?? "Error al guardar los cambios.";
        enqueueSnackbar(msg, { variant: "error" });
      }
    },
  });

  const sectionDirty = useSectionDirty(formik.values, formik.initialValues);
  const sectionFaltantes = useSectionCompleteness(formik.values);
  const { requestLeave, dialogOpen, onSeguirEditando, onDescartar } =
    useConfirmDiscard({ dirty: formik.dirty });

  useAnchorScroll();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [camposIA, setCamposIA] = useState(new Set());
  const desmarcarCampoIA = (c) =>
    setCamposIA((prev) => {
      const next = new Set(prev);
      next.delete(c);
      return next;
    });

  const isXL = useMediaQuery("(min-width:1600px)");
  const isMedium = useMediaQuery("(min-width:1280px)");

  const salirAlDetalle = () =>
    requestLeave(() => navigate(`/dashboard/propuestas/v2/${propuesta.id}`));

  const previewShape = buildPropuestaPreview(formik.values);
  const fasesPreview = mapItemsFormAFases(formik.values.items);

  return (
    <FormikProvider value={formik}>
      {/* Altura fija = viewport - DashboardNavbar (APP_BAR_MOBILE=64, APP_BAR_DESKTOP=92).
          El scroll vive DENTRO de la columna central (patrón "freeze panes"): TopBar,
          Banner y NavLateral quedan pinned sin depender de position: sticky, que no
          funciona bien con MainStyle (overflow: auto) del dashboard layout. */}
      <Box
        sx={{
          display: "flex",
          height: { xs: "calc(100vh - 64px)", lg: "calc(100vh - 92px)" },
          overflow: "hidden",
        }}
      >
        {isMedium && (
          <NavLateralEdicion
            itemsCount={formik.values.items.length}
            equipoCount={formik.values.equipo.length}
            sectionDirty={sectionDirty}
            sectionFaltantes={sectionFaltantes}
          />
        )}

        {!isMedium && (
          <Drawer open={navOpen} onClose={() => setNavOpen(false)}>
            <NavLateralEdicion
              itemsCount={formik.values.items.length}
              equipoCount={formik.values.equipo.length}
              sectionDirty={sectionDirty}
              sectionFaltantes={sectionFaltantes}
              onNavegar={() => setNavOpen(false)}
            />
          </Drawer>
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <TopBarEdicion
            id={propuesta.id}
            dirty={formik.dirty}
            isSubmitting={isSubmitting}
            isValid={formik.isValid}
            onCancelar={salirAlDetalle}
            onGuardar={formik.handleSubmit}
            onAbrirPreview={!isXL ? () => setPreviewOpen(true) : undefined}
            onAbrirNav={!isMedium ? () => setNavOpen(true) : undefined}
            showPreviewButton={!isXL}
            showNavButton={!isMedium}
            generadoConIA={Boolean(formik.values.generadoConIA)}
            onAbrirRegenerar={() => setRegenerarOpen(true)}
          />

          <BannerCambiosSinGuardar
            visible={formik.dirty}
            mensaje={
              <>
                Tienes cambios sin guardar. No olvides presionar{" "}
                <strong>Guardar cambios</strong> antes de cambiar de sección.
              </>
            }
          />

          {/* Zona scrollable — es el único elemento que scrolea en la vista. */}
          <Box
            id={SCROLL_CONTAINER_ID}
            sx={{ flex: 1, overflowY: "auto", p: 3 }}
          >
            <SeccionPortada
              formik={formik}
              camposIA={camposIA}
              desmarcarCampoIA={desmarcarCampoIA}
              faltantes={sectionFaltantes.portada}
            />
            <SeccionQuienesSomos
              formik={formik}
              faltantes={sectionFaltantes.quienesSomos}
            />
            <SeccionAntecedentes
              formik={formik}
              camposIA={camposIA}
              desmarcarCampoIA={desmarcarCampoIA}
              faltantes={sectionFaltantes.antecedentes}
            />
            <SeccionEstructura
              formik={formik}
              camposIA={camposIA}
              desmarcarCampoIA={desmarcarCampoIA}
              faltantes={sectionFaltantes.estructura}
            />
            <SeccionFasesServicios
              formik={formik}
              faltantes={sectionFaltantes.fasesServicios}
              razonesSeleccionIA={razonesSeleccionIA}
              limpiarRazonSeleccionIA={limpiarRazonSeleccionIA}
              resetRazonesSeleccionIA={resetRazonesSeleccionIA}
              swapRazonesSeleccionIA={swapRazonesSeleccionIA}
            />
            <SeccionEquipo
              formik={formik}
              faltantes={sectionFaltantes.equipo}
            />
            <SeccionInfoAdicional
              formik={formik}
              faltantes={sectionFaltantes.infoAdicional}
            />
          </Box>
        </Box>

        {isXL && (
          <Box
            sx={{
              width: 480,
              flexShrink: 0,
              borderLeft: "1px solid",
              borderColor: "divider",
              height: "100%",
              overflowY: "auto",
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Icon icon={eyeFill} width={16} height={16} />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontWeight: 600 }}
              >
                Vista previa (en vivo)
              </Typography>
            </Box>
            <ProposalPreview
              propuesta={previewShape}
              fases={fasesPreview}
              equipo={formik.values.equipo}
            />
          </Box>
        )}

        {!isXL && (
          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            fullWidth
            maxWidth="md"
            scroll="paper"
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "divider",
                pb: 1.5,
              }}
            >
              <Typography variant="h6">Vista previa de la propuesta</Typography>
              <IconButton size="small" onClick={() => setPreviewOpen(false)}>
                <Icon icon={closeFill} width={20} height={20} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              <ProposalPreview
                propuesta={previewShape}
                fases={fasesPreview}
                equipo={formik.values.equipo}
              />
            </DialogContent>
          </Dialog>
        )}

        <DialogDescartarCambios
          open={dialogOpen}
          onSeguir={onSeguirEditando}
          onDescartar={onDescartar}
        />
      </Box>
      <RegenerarIADialog
        open={regenerarOpen}
        onClose={() => setRegenerarOpen(false)}
        promptOriginal={formik.values.promptOriginal || ""}
        onRegenerar={handleRegenerar}
        isLoading={isRegenerando}
      />
    </FormikProvider>
  );
};

EditarPropuestaV2.propTypes = {
  propuesta: PropTypes.object.isRequired,
};

export default EditarPropuestaV2;
